<script setup lang="ts">
import AppTabs from "@/components/shared/AppTabs.vue";
import CatMascot from "@/components/doctor-performance/cats/CatMascot.vue";
import PetCourier from "@/components/smart-warehouse/PetCourier.vue";
import SmartWarehouseItemDrawer from "@/components/smart-warehouse/SmartWarehouseItemDrawer.vue";
import SmartWarehouseOrderDialog from "@/components/smart-warehouse/SmartWarehouseOrderDialog.vue";
import SmartWarehouseSettingsDialog from "@/components/smart-warehouse/SmartWarehouseSettingsDialog.vue";
import WarehousePetScene from "@/components/smart-warehouse/WarehousePetScene.vue";
import { usePetPhrases } from "@/components/smart-warehouse/smartWarehouseUi";
import { createSmartWarehouse, provideSmartWarehouse } from "@/composables/useSmartWarehouse";
import type { TabItem } from "@/type/tabs";
import { WH_PERIOD_PRESETS } from "@/utils/smartWarehouse";

/**
 * Smart warehouse — what is running out, what sells and what sleeps on the shelves, which
 * transfers are stuck, what to order from which supplier, and who gets a WhatsApp alert about it.
 * Thirteen tabs share one filter bar and one loaded dashboard (`useSmartWarehouse`); every rule is
 * computed on the page in `utils/smartWarehouse.ts`. Until `get_smart_warehouse_dashboard` is
 * deployed the page runs on sample data and says so with a chip; see
 * docs/README.smart-warehouse-backend-prompt.md.
 */

definePage({ meta: { permissionKey: "page.warehouse.smart_dashboard" } });

const { t } = useI18n({ useScope: "global" });
const { phrase } = usePetPhrases();

const ctx = createSmartWarehouse();
provideSmartWarehouse(ctx);

const TABS: TabItem[] = [
  { value: "overview", labelKey: "alkokh.smartWarehouse.tabs.overview", icon: "tabler-layout-dashboard", loader: () => import("@/components/smart-warehouse/tabs/SmartWarehouseOverviewTab.vue") },
  { value: "vetcheck", labelKey: "alkokh.smartWarehouse.tabs.vetcheck", icon: "tabler-stethoscope", loader: () => import("@/components/smart-warehouse/tabs/SmartWarehouseVetCheckTab.vue") },
  { value: "alerts", labelKey: "alkokh.smartWarehouse.tabs.alerts", icon: "tabler-bell-ringing", loader: () => import("@/components/smart-warehouse/tabs/SmartWarehouseAlertsTab.vue") },
  { value: "sellers", labelKey: "alkokh.smartWarehouse.tabs.sellers", icon: "tabler-trophy", loader: () => import("@/components/smart-warehouse/tabs/SmartWarehouseSellersTab.vue") },
  { value: "transfers", labelKey: "alkokh.smartWarehouse.tabs.transfers", icon: "tabler-truck-delivery", loader: () => import("@/components/smart-warehouse/tabs/SmartWarehouseTransfersTab.vue") },
  { value: "reorder", labelKey: "alkokh.smartWarehouse.tabs.reorder", icon: "tabler-shopping-cart-plus", loader: () => import("@/components/smart-warehouse/tabs/SmartWarehouseReorderTab.vue") },
  { value: "suppliers", labelKey: "alkokh.smartWarehouse.tabs.suppliers", icon: "tabler-building-store", loader: () => import("@/components/smart-warehouse/tabs/SmartWarehouseSuppliersTab.vue") },
  { value: "forecast", labelKey: "alkokh.smartWarehouse.tabs.forecast", icon: "tabler-calendar-stats", loader: () => import("@/components/smart-warehouse/tabs/SmartWarehouseForecastTab.vue") },
  { value: "whatif", labelKey: "alkokh.smartWarehouse.tabs.whatif", icon: "tabler-adjustments-horizontal", loader: () => import("@/components/smart-warehouse/tabs/SmartWarehouseWhatIfTab.vue") },
  { value: "expiry", labelKey: "alkokh.smartWarehouse.tabs.expiry", icon: "tabler-calendar-time", loader: () => import("@/components/smart-warehouse/tabs/SmartWarehouseExpiryTab.vue") },
  { value: "combos", labelKey: "alkokh.smartWarehouse.tabs.combos", icon: "tabler-heart-handshake", loader: () => import("@/components/smart-warehouse/tabs/SmartWarehouseCombosTab.vue") },
  { value: "missions", labelKey: "alkokh.smartWarehouse.tabs.missions", icon: "tabler-paw", loader: () => import("@/components/smart-warehouse/tabs/SmartWarehouseMissionsTab.vue") },
  { value: "suggestions", labelKey: "alkokh.smartWarehouse.tabs.suggestions", icon: "tabler-bulb", loader: () => import("@/components/smart-warehouse/tabs/SmartWarehouseSuggestionsTab.vue") },
];

const presetItems = computed(() => WH_PERIOD_PRESETS.map(value => ({ value, title: t(`alkokh.smartWarehouse.period.${value}`) })));

// The header line changes every few seconds.
const headerLine = ref(0);
let ticker: ReturnType<typeof setInterval> | null = null;
onMounted(() => { ticker = setInterval(() => { headerLine.value += 1; }, 8000); });
onBeforeUnmount(() => { if (ticker) clearInterval(ticker); });
const headerText = computed(() => phrase(ctx.attention.value.length ? "busy" : "header", headerLine.value, {
  count: ctx.attention.value.length,
  name: ctx.attention.value[0]?.item.item_name ?? "",
}));

const DEFINITIONS = ["velocity", "cover", "reorderPoint", "suggested", "status", "abc", "supplierScore", "stuck"] as const;
const definitionParams = computed(() => {
  const s = ctx.settings.value;

  return {
    velocityDays: s.stock.velocityDays,
    safety: s.stock.safetyDays,
    lead: s.stock.defaultLeadDays,
    target: s.stock.coverTargetDays,
    dead: s.stock.deadStockDays,
    over: s.stock.overstockDays,
    stuck: s.transfers.stuckAfterDays,
    price: s.suppliers.weights.price,
    speed: s.suppliers.weights.speed,
    reliability: s.suppliers.weights.reliability,
  };
});

// Counts on the tabs that need action. The loaders are reused, so a count change never reloads a tab.
const tabs = computed<TabItem[]>(() => {
  const badges: Record<string, { n: number; color: string }> = {
    alerts: { n: ctx.alertCount.value, color: "error" },
    transfers: { n: ctx.stuckTransfers.value.length, color: "warning" },
    reorder: { n: ctx.orderGroups.value.reduce((s, g) => s + g.lines.length, 0), color: "primary" },
    expiry: { n: ctx.expiry.value.filter(e => e.action !== "watch").length, color: "warning" },
    missions: { n: ctx.missions.value.filter(m => !ctx.missionsDone.value.includes(m.id)).length, color: "success" },
  };

  return TABS.map(tab => (badges[tab.value!]?.n ? { ...tab, badge: badges[tab.value!].n, badgeColor: badges[tab.value!].color } : tab));
});

const HEALTH_COLOR = { great: "success", good: "info", fair: "warning", sick: "error" } as const;
const healthColor = computed(() => HEALTH_COLOR[ctx.health.value.grade]);

const showTabs = computed(() => ctx.state.value === "ok" && ctx.loaded.value);
const firstLoad = computed(() => !ctx.loaded.value);
</script>

<template>
  <div class="sw-page">
    <VCard
      elevation="0"
      border
      class="sw-header mb-4"
    >
      <div class="sw-head">
        <div class="sw-head__text">
          <h1 class="text-h4 d-flex align-center gap-2 flex-wrap">
            {{ t("alkokh.smartWarehouse.title") }}
            <VChip
              v-if="ctx.report.value.source === 'mock'"
              color="warning"
              variant="tonal"
              size="small"
              prepend-icon="tabler-flask"
            >
              {{ t("alkokh.smartWarehouse.sampleData") }}
            </VChip>
            <VChip
              v-if="showTabs"
              :color="healthColor"
              variant="flat"
              size="small"
              prepend-icon="tabler-heartbeat"
              class="sw-head__alerts"
              @click="ctx.openTab('vetcheck')"
            >
              {{ t("alkokh.smartWarehouse.healthChip", { score: ctx.health.value.score }) }}
            </VChip>
            <VChip
              v-if="showTabs"
              :color="ctx.settings.value.alerts.enabled ? 'success' : 'default'"
              variant="tonal"
              size="small"
              prepend-icon="tabler-brand-whatsapp"
              class="sw-head__alerts"
              @click="ctx.openSettings('alerts')"
            >
              {{ ctx.settings.value.alerts.enabled ? t("alkokh.smartWarehouse.alertsOn", { count: ctx.recipients.value.filter(r => r.reachable).length }) : t("alkokh.smartWarehouse.alertsOff") }}
            </VChip>
          </h1>
          <p class="text-body-2 text-medium-emphasis mb-2">
            {{ t("alkokh.smartWarehouse.subtitle") }}
          </p>
          <p
            v-if="showTabs"
            class="sw-head__line text-body-1 mb-0"
          >
            <VIcon
              icon="tabler-sparkles"
              size="18"
              color="warning"
            />
            <Transition
              name="sw-fade"
              mode="out-in"
            >
              <span :key="headerText">{{ headerText }}</span>
            </Transition>
          </p>
        </div>
        <WarehousePetScene
          v-if="showTabs"
          class="sw-head__scene"
        />
      </div>

      <div class="sw-filters">
        <AppSelect
          v-model="ctx.preset.value"
          :items="presetItems"
          density="compact"
          :label="t('alkokh.smartWarehouse.period.label')"
          class="sw-filters__item"
        />
        <template v-if="ctx.preset.value === 'custom'">
          <AppDateTimePicker
            v-model="ctx.customFrom.value"
            density="compact"
            :label="t('alkokh.smartWarehouse.period.from')"
            :config="{ enableTime: false, dateFormat: 'Y-m-d' }"
            class="sw-filters__item"
          />
          <AppDateTimePicker
            v-model="ctx.customTo.value"
            density="compact"
            :label="t('alkokh.smartWarehouse.period.to')"
            :config="{ enableTime: false, dateFormat: 'Y-m-d' }"
            class="sw-filters__item"
          />
        </template>
        <AppSelect
          v-model="ctx.warehouses.value"
          :items="ctx.warehouseItems.value"
          multiple
          chips
          closable-chips
          clearable
          density="compact"
          :label="t('alkokh.smartWarehouse.filters.warehouses')"
          :placeholder="t('alkokh.smartWarehouse.filters.allWarehouses')"
          class="sw-filters__item sw-filters__item--wide"
        />
        <AppSelect
          v-model="ctx.itemGroups.value"
          :items="ctx.itemGroupItems.value"
          multiple
          chips
          closable-chips
          clearable
          density="compact"
          :label="t('alkokh.smartWarehouse.filters.itemGroups')"
          :placeholder="t('alkokh.smartWarehouse.filters.allItemGroups')"
          class="sw-filters__item sw-filters__item--wide"
        />
        <div class="sw-filters__actions">
          <VProgressCircular
            v-if="ctx.loading.value"
            indeterminate
            size="20"
            width="2"
            color="primary"
          />
          <VBtn
            icon="tabler-refresh"
            variant="text"
            size="small"
            :aria-label="t('alkokh.smartWarehouse.actions.refresh')"
            @click="ctx.load()"
          />
          <VBtn
            icon="tabler-settings"
            variant="text"
            size="small"
            :aria-label="t('alkokh.smartWarehouse.actions.settings')"
            @click="ctx.openSettings()"
          />
          <VMenu
            location="bottom end"
            max-width="440"
          >
            <template #activator="{ props: menu }">
              <VBtn
                v-bind="menu"
                icon="tabler-info-circle"
                variant="text"
                size="small"
                :aria-label="t('alkokh.smartWarehouse.definitions.title')"
              />
            </template>
            <VCard>
              <VCardItem>
                <VCardTitle>{{ t("alkokh.smartWarehouse.definitions.title") }}</VCardTitle>
                <VCardSubtitle class="text-wrap">
                  {{ t("alkokh.smartWarehouse.definitions.subtitle") }}
                </VCardSubtitle>
              </VCardItem>
              <VCardText>
                <div
                  v-for="key in DEFINITIONS"
                  :key="key"
                  class="sw-def"
                >
                  <div class="font-weight-bold text-body-2">
                    {{ t(`alkokh.smartWarehouse.definitions.${key}.title`) }}
                  </div>
                  <div class="text-caption text-medium-emphasis">
                    {{ t(`alkokh.smartWarehouse.definitions.${key}.body`, definitionParams) }}
                  </div>
                </div>
              </VCardText>
            </VCard>
          </VMenu>
        </div>
      </div>
    </VCard>

    <AppTabs
      v-if="showTabs"
      v-model="ctx.activeTab.value"
      :items="tabs"
      variant="underline"
      sync-mode="query"
      sync-key="tab"
      default-tab="overview"
      card
      tabs-class="px-4"
    />

    <!-- Before the first answer, and when it fails, there are no tabs to show. -->
    <VCard
      v-else
      elevation="0"
      border
    >
      <VCardText class="sw-state">
        <template v-if="firstLoad">
          <PetCourier
            pose="walk"
            color="golden"
            accessory="cap"
            :size="130"
          />
          <span class="text-medium-emphasis">{{ t("alkokh.smartWarehouse.states.loading") }}</span>
        </template>
        <template v-else-if="ctx.state.value === 'not_deployed'">
          <PetCourier
            pose="sleep"
            color="brown"
            :size="130"
          />
          <VAlert
            type="info"
            variant="tonal"
            icon="tabler-info-circle"
          >
            {{ t("alkokh.smartWarehouse.states.not_deployed") }}
          </VAlert>
        </template>
        <template v-else>
          <CatMascot
            pose="sit"
            mood="grumpy"
            color="black"
            :size="120"
          />
          <VAlert
            type="error"
            variant="tonal"
          >
            {{ t("alkokh.smartWarehouse.states.failed") }}
            <template #append>
              <VBtn
                size="small"
                variant="text"
                @click="ctx.load()"
              >
                {{ t("alkokh.smartWarehouse.actions.retry") }}
              </VBtn>
            </template>
          </VAlert>
        </template>
      </VCardText>
    </VCard>

    <SmartWarehouseItemDrawer />
    <SmartWarehouseOrderDialog />
    <SmartWarehouseSettingsDialog />
  </div>
</template>

<style scoped>
.sw-page :deep(.app-tabs__window) {
  margin-block-start: 16px;
  overflow: visible;
}

.sw-head {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  overflow: hidden;
  padding-block: 20px 8px;
  padding-inline: 20px;
  background:
    radial-gradient(circle at 90% 30%, rgba(var(--v-theme-success), 0.16), transparent 40%),
    radial-gradient(circle at 70% 100%, rgba(var(--v-theme-warning), 0.14), transparent 42%),
    radial-gradient(circle at 0% 0%, rgba(var(--v-theme-primary), 0.14), transparent 45%);
  background-size: 140% 140%;
  animation: sw-glow 16s ease-in-out infinite alternate;
}

.sw-head__text {
  flex: 1 1 320px;
  padding-block-end: 12px;
}

.sw-head__alerts {
  cursor: pointer;
}

.sw-head__line {
  display: flex;
  align-items: center;
  gap: 8px;
  min-block-size: 1.6em;
  font-weight: 500;
}

.sw-head__scene {
  flex: 0 1 420px;
}

.sw-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  padding-block: 14px;
  padding-inline: 20px;
  border-block-start: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.sw-filters__item {
  flex: 1 1 170px;
  max-inline-size: 230px;
}

.sw-filters__item--wide {
  max-inline-size: 320px;
}

.sw-filters__actions {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-inline-start: auto;
}

.sw-def {
  margin-block-end: 10px;
}

.sw-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding-block: 48px;
}

.sw-fade-enter-active,
.sw-fade-leave-active {
  transition: opacity 0.35s ease, transform 0.35s ease;
}

.sw-fade-enter-from,
.sw-fade-leave-to {
  opacity: 0;
  transform: translateY(6px);
}

@keyframes sw-glow {
  to { background-position: 100% 100%; }
}

@media (max-width: 959px) {
  .sw-head__scene {
    display: none;
  }

  .sw-filters__item,
  .sw-filters__item--wide {
    max-inline-size: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .sw-head {
    animation: none;
  }
}
</style>

<!-- Motion shared by the tabs (they are separate components, so this cannot be scoped). -->
<style>
.sw-rise {
  animation: sw-rise 0.5s cubic-bezier(0.2, 0.9, 0.3, 1) both;
}

.sw-list-move,
.sw-list-enter-active,
.sw-list-leave-active {
  transition: transform 0.55s cubic-bezier(0.2, 0.9, 0.3, 1), opacity 0.4s ease;
}

.sw-list-enter-from,
.sw-list-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

@keyframes sw-rise {
  from {
    opacity: 0;
    transform: translateY(14px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .sw-rise {
    animation: none;
  }

  .sw-list-move,
  .sw-list-enter-active,
  .sw-list-leave-active {
    transition: none;
  }
}
</style>
